import React, { useState, useEffect, useContext } from "react";
import useToastContext from "hooks/useToastContext";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import { useTranslation } from "react-i18next";
import OInputField from "components/reusable/OInputField";
import formValidation from "utils/formValidation";
import AuthContext from "context/AuthContext";
import ErrorMessage from "components/ErrorMessage";
import OImage from "components/reusable/OImage";
import noImageFound from "../../assets/images/No-image-found.jpg";
import { apiGet } from "../../utils/apiFetch";
import { isEmpty } from "lodash";
import ODateRangePicker from "components/shared/datePicker/ODateRangePicker";

const EditCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const notification = useToastContext();
  const { updatePageName } = useContext(AuthContext);
  const [logo, setLogo] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const storedLogo = location?.state?.logo;
  const storedProfilePicture = location?.state?.profilePicture;
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [states, setStates] = useState([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: {
      name: location?.state?.name,
      officialEmail: location?.state?.officialEmail,
      countryID: location?.state?.Country?._id,
      stateID: location?.state?.State?._id,
      city:location?.state?.city,
      zipcode:location?.state?.zipcode,
      // cityID: location?.state?.City?._id,
      subscriptionID: location?.state?.Subscription?.subscriptionId,
      companyNumber: location?.state?.companyNumber,
      address: location?.state?.address,
      status: location?.state?.status,
      role: location?.state?.role,
      subscriptionPurchaseDate:
      location?.state?.Subscription?.subscriptionPurchaseDate?.split("T")[0],
      subscriptionExpiryDate:
      location?.state?.Subscription?.subscriptionExpiryDate?.split("T")[0],
    },
  });

  const countryID = watch("countryID");

  // Add this useEffect hook
  useEffect(() => {
    if (countryID) {
      getStateListByCountry(countryID);
    } else {
      setStates([]);
    }
  }, [countryID]);

  useEffect(() => {
    updatePageName(t("Edit Company"));
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      setIsLoading(true);
      // Fetch countries
      const countriesRes = await apiGet(apiPath.getCountryList);
      if (countriesRes.data.success) {
        setCountries(countriesRes.data.results.allCountries);
      }

      // Fetch cities
      const citiesRes = await apiGet(apiPath.cityList);
      if (citiesRes.data.success) {
        setCities(citiesRes.data.results.docs);
      }

      // Fetch subscriptions
      const subscriptionsRes = await apiGet(apiPath.getSubscriptionList);
      if (subscriptionsRes.data.success) {
        setSubscriptions(subscriptionsRes.data.results.docs);
      }
    } catch (error) {
      notification.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };
   const getStateListByCountry = async (countryId)=>{
     try {
      const res = await apiGet(`${apiPath.getStateListByCountry}/${countryId}`, {});
      setStates(res?.data?.results?.allStates);
    } catch (error) {
      console.log("error:", error);
    }
  }

  // Set dropdown values after data is loaded
  useEffect(() => {
    if (!isLoading) {
      if (location?.state?.Country?._id) {
        setValue("countryID", location?.state?.Country?._id);
        getStateListByCountry(location?.state?.Country?._id)
      }
      if (location?.state?.State?._id) {
        setValue("stateID", location?.state?.State?._id);
      }
      if (location?.state?.City?._id) {
        setValue("cityID", location?.state?.City?._id);
      }
      if (location?.state?.Subscription?.subscriptionId) {
        setValue(
          "subscriptionID",
          location?.state?.Subscription?.subscriptionId
        );
      }
    }
  }, [isLoading, location?.state, setValue]);

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      // Append all form fields except files
      formData.append("name", data?.name?.trim());
      formData.append("officialEmail", data?.officialEmail?.trim());
      formData.append("countryID", data?.countryID);
      formData.append("stateID", data?.stateID);
      formData.append("city", data?.city);
      formData.append("zipcode", data?.zipcode);
      formData.append("subscriptionID", data?.subscriptionID);
      formData.append("companyNumber", data?.companyNumber?.trim());
      formData.append("address", data?.address?.trim());
      formData.append("status", data?.status);
      formData.append("role", data?.role?.trim());

      // Append files if they exist
      if (logo) {
        formData.append("logo", logo);
      }
      if (profilePicture) {
        formData.append("profilePicture", profilePicture);
      }

      // Append subscription dates if subscriptionID exists
      if (data.subscriptionID) {
        if (data.subscriptionPurchaseDate) {
          formData.append(
            "subscriptionPurchaseDate",
            data.subscriptionPurchaseDate
          );
        }
        if (data.subscriptionExpiryDate) {
          formData.append(
            "subscriptionExpiryDate",
            data.subscriptionExpiryDate
          );
        }
      }

      const path = apiPath.editCompany + "/" + location?.state?._id;
      const result = await apiPut(path, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (result.data.success) {
        navigate("/company-manager");
        notification.success("Company Update Successfully");
      } else {
        notification.error(result?.data.message);
      }
    } catch (error) {
      notification.error(error.message);
    }
  };

  // Handle image display
  const getLogoImageSrc = () => {
    if (logo) {
      return typeof logo === "object" ? URL.createObjectURL(logo) : logo;
    }
    return storedLogo || noImageFound;
  };

  const getProfileImageSrc = () => {
    if (profilePicture) {
      return typeof profilePicture === "object"
        ? URL.createObjectURL(profilePicture)
        : profilePicture;
    }
    return storedProfilePicture || noImageFound;
  };

  useEffect(() => {
    return () => {
      if (logo && typeof logo === "object") {
        URL.revokeObjectURL(logo);
      }
      if (profilePicture && typeof profilePicture === "object") {
        URL.revokeObjectURL(profilePicture);
      }
    };
  }, [logo, profilePicture]);

  if (isLoading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  return (
    <>
      <div className="bg-[#F9F9F9]">
        <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
          <div className="px-3 py-4">
            <div className="bg-white border border-[#E9EDF9] rounded-lg">
              <div className="p-5 shadow-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo Upload */}
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      <div>
                        <label className="mb-2 block pr-1">
                          <strong>{t("COMPANY_LOGO")}</strong>
                        </label>
                        <input
                          type="file"
                          name="logo"
                          className="form-control"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={(e) =>
                            e.target.files?.[0] && setLogo(e.target.files[0])
                          }
                        />
                      </div>
                      {logo && (
                        <button
                          type="button"
                          onClick={() => {
                            setLogo(null);
                            document.querySelector('input[type="file"]').value =
                              "";
                          }}
                          className="text-black bg-[#E1E1E1] font-normal px-4 py-2 text-sm outline-none focus:outline-none rounded ease-linear transition-all duration-150 hover:bg-gray-300 mt-6"
                        >
                          {t("CANCEL")}
                        </button>
                      )}
                    </div>
                    <OImage
                      src={getLogoImageSrc()}
                      fallbackUrl={noImageFound}
                      className="max-w-[200px]"
                    />
                  </div>

                  {/* Profile Picture Upload */}
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      <div>
                        <label className="mb-2 block pr-1">
                          <strong>{t("PROFILE_PICTURE")}</strong>
                        </label>
                        <input
                          type="file"
                          name="profilePicture"
                          className="form-control"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={(e) =>
                            e.target.files?.[0] &&
                            setProfilePicture(e.target.files[0])
                          }
                        />
                      </div>
                      {profilePicture && (
                        <button
                          type="button"
                          onClick={() => {
                            setProfilePicture(null);
                            document.querySelectorAll(
                              'input[type="file"]'
                            )[1].value = "";
                          }}
                          className="text-black bg-[#E1E1E1] font-normal px-4 py-2 text-sm outline-none focus:outline-none rounded ease-linear transition-all duration-150 hover:bg-gray-300 mt-6"
                        >
                          {t("CANCEL")}
                        </button>
                      )}
                    </div>
                    <OImage
                      src={getProfileImageSrc()}
                      fallbackUrl={noImageFound}
                      className="max-w-[200px]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <OInputField
                    inputLabel={
                      <>
                        {t("COMPANY_NAME")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    wrapperClassName="relative z-0 mb-6 w-full group"
                    name="name"
                    type="text"
                    autoFocus
                    register={register("name", formValidation["name"])}
                    errors={errors}
                  />

                  <OInputField
                    inputLabel={
                      <>
                        {t("OFFICIAL_EMAIL")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    wrapperClassName="relative z-0 mb-6 w-full group"
                    name="officialEmail"
                    type="email"
                    register={register(
                      "officialEmail",
                      formValidation["email"]
                    )}
                    errors={errors}
                  />

                  <div className="relative z-0 mb-6 w-full group">
                    <label className="block mb-2 text-sm font-medium text-gray-900">
                      {t("COUNTRY")}
                    </label>
                    <select
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      {...register("countryID")}
                      value={watch("countryID") || ""}
                    >
                      <option value="">{t("SELECT_COUNTRY")}</option>
                      {countries.map((country) => (
                        <option key={country._id} value={country._id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="relative z-0 mb-6 w-full group">
                    <label className="block mb-2 text-sm font-medium text-gray-900">
                      {t("STATE")}
                    </label>
                    <select
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      {...register("stateID")}
                      value={watch("stateID") || ""}
                    >
                      <option value="">{t("SELECT_STATE")}</option>
                      {states.map((state) => (
                        <option key={state._id} value={state._id}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <OInputField
                    inputLabel={
                      <>
                        {t("CITY")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    wrapperClassName="relative z-0 mb-6 w-full group"
                    name="city"
                    type="text"
                    register={register(
                      "city",
                      formValidation["city"]
                    )}
                    errors={errors}
                  />
                  <OInputField
                    inputLabel={
                      <>
                        {t("ZIPCODE")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    wrapperClassName="relative z-0 mb-6 w-full group"
                    name="zipcode"
                    type="text"
                    register={register(
                      "zipcode",
                      formValidation["zipcode"]
                    )}
                    errors={errors}
                  />
                  <OInputField
                    inputLabel={
                      <>
                        {t("COMPANY_NUMBER")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    wrapperClassName="relative z-0 mb-6 w-full group"
                    name="companyNumber"
                    type="text"
                    register={register(
                      "companyNumber",
                      formValidation["companyNumber"]
                    )}
                    errors={errors}
                  />

                  <OInputField
                    inputLabel={
                      <>
                        {t("ADDRESS")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    wrapperClassName="relative z-0 mb-6 w-full group"
                    name="address"
                    type="text"
                    register={register("address", formValidation["address"])}
                    errors={errors}
                    maxLength={100}
                  />

                  <div className="relative z-0 mb-6 w-full group">
                    <label className="block mb-2 text-sm font-medium text-gray-900">
                      {t("Subscription")}
                    </label>
                    <input
                      type="text"
                      disabled
                      className="bg-gray-100 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 cursor-not-allowed"
                      value={
                        subscriptions.find(
                          (sub) => sub._id === watch("subscriptionID")
                        )?.name || "No Subscription Selected"
                      }
                      onChange={() => {}} // Empty handler to prevent React warnings
                    />
                  </div>

                  <div className="relative z-0 mb-6 w-full group">
                    <label className="block mb-2 text-sm font-medium text-gray-900">
                      {t("Status")}
                    </label>
                    <select
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      {...register("status")}
                    >
                      <option key="active" value="active">{t("ACTIVE")}</option>
                      <option key="inactive" value="inactive">{t("INACTIVE")}</option>
                    </select>
                  </div>

                  <OInputField
                    inputLabel={
                      <>
                        {t("ROLE")}
                        <span className="text-red-500">*</span>
                      </>
                    }
                    wrapperClassName="relative z-0 mb-6 w-full group"
                    name="role"
                    type="text"
                    register={register("role", formValidation["role"])}
                    errors={errors}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
            <button
              className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150"
              type="button"
              onClick={() => navigate("/company-manager")}
            >
              {t("O_BACK")}
            </button>

            <button
              className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
              type="submit"
            >
              {t("O_UPDATE")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditCompany;
